import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from "api/axiosClient";

const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;
const userBaseURL = `${REACT_APP_CARGOBASE_BASE_URL}/user`;
const scheduledReportsBaseURL = `${REACT_APP_CARGOBASE_BASE_URL}/users/scheduled-reports`;

/**
 * Generates a query string
 * @param {String} id the payment location id to query by
 * @returns {String} the query string
 */
const generateQuery = (id) => (id !== "All" && id ? `?payment_location=${id}` : "");

const acceptLegalAgreements = () => postRequest(`${userBaseURL}/legal-agreements`, {});
const addAddress = (payload) => postRequest(`${userBaseURL}/address-book`, payload);
const addContact = (payload) => postRequest(`${userBaseURL}/contacts`, payload);
const createApprovalLevel = (payload) => postRequest(`${userBaseURL}/admin/settings/approval-levels/create`, payload);
const createNewUserRole = (payload) => postRequest(`${userBaseURL}/admin/settings/roles/create`, payload);
const createShipmentTemplate = (payload) => postRequest(`${userBaseURL}/templates`, payload);
const createNewPaymentLocation = (payload) => postRequest(`${userBaseURL}/admin/settings/payment-locations/create`, payload);
const createPrefill = (payload) => postRequest(`${userBaseURL}/admin/settings/prefill/create`, payload);
const deleteAddress = (id) => deleteRequest(`${userBaseURL}/address-book/${id}`);
const deleteApprovalLevel = (id) => deleteRequest(`${userBaseURL}/admin/settings/approval-levels/${id}`);
const deleteAllApprovalLevels = (id) =>
  deleteRequest(`${userBaseURL}/admin/settings/approval-levels/payment-locations/${id}`);
const deleteContact = (id) => deleteRequest(`${userBaseURL}/contacts/${id}`);
const deletePrefill = (id) => deleteRequest(`${userBaseURL}/admin/settings/prefill/${id}`);
const deleteProvider = (location, provider) =>
  deleteRequest(`${userBaseURL}/admin/settings/providers/${location}/${provider}`);
const deleteShipmentTemplate = (id) => deleteRequest(`${userBaseURL}/templates/${id}`);
const exportProviderTeams = () => getRequest(`${userBaseURL}/provider-team/export`);
const getDPA = () => getRequest(`${userBaseURL}/download-dpa`, { responseType: "blob" });
const getAllReviews = (config = {}) => getRequest(`${userBaseURL}/admin/settings/providers/export-all-reviews`, config);
const getAllProviderContacts = (config = {}) =>
  getRequest(`${userBaseURL}/admin/settings/provider/export-user-contacts`, config);
const getAddress = (id) => getRequest(`${userBaseURL}/address-book/${id}`);
const getAddressBook = (config = {}) => getRequest(`${userBaseURL}/address-book`, config);
const getApprovalLevelCreateOptions = () => getRequest(`${userBaseURL}/admin/settings/approval-levels/create`);
const getApprovalLevelDetails = (id) => getRequest(`${userBaseURL}/admin/settings/approval-levels/${id}`);
const getCompanyApprovalLevels = (config = {}) => getRequest(`${userBaseURL}/admin/settings/approval-levels`, config);
const getContact = (id) => getRequest(`${userBaseURL}/contacts/${id}`);
const getContactBook = (config = {}) => getRequest(`${userBaseURL}/contacts`, config);
const getCurrentUser = () => getRequest(userBaseURL);
const getInvoiceSettings = () => getRequest(`${userBaseURL}/admin/settings/company/invoice`);
const getLocationApprovalLevels = (config = {}) =>
  getRequest(`${userBaseURL}/admin/settings/approval-levels/payment-locations`, config);
const getNewUserRoles = () => getRequest(`${userBaseURL}/admin/settings/roles/create`);
const getNotifications = (id) => getRequest(`${userBaseURL}/notifications${generateQuery(id)}`);
const markNotificationAsRead = (id) => patchRequest(`${userBaseURL}/notifications/${id}/read`, {});
const markAllNotificationsAsRead = () => putRequest(`${userBaseURL}/notifications/mark-all-as-read`);
const getNotificationSettings = () => getRequest(`${userBaseURL}/settings/notifications`);
const getRecentlyDeliveredShipments = (id) => getRequest(`${userBaseURL}/recently-delivered-shipments${generateQuery(id)}`);
const getUsers = (config = {}) => getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/users`, config);
const getPermissions = () => getRequest(`${userBaseURL}/permissions`);
const getPreferredContact = (addressId) => getRequest(`${userBaseURL}/favorite-address/${addressId}/preferred-contact`);
const getPrefills = (config = {}) => getRequest(`${userBaseURL}/admin/settings/prefills`, config);
const getPrefillDetails = (id) => getRequest(`${userBaseURL}/admin/settings/prefill/${id}`);
const getProvidersList = (config) => getRequest(`${userBaseURL}/admin/settings/providers/export-list`, config);
const getProviderTeams = (config) => getRequest(`${userBaseURL}/provider-teams`, config);
const getProviderTeamsDetail = (id) => getRequest(`${userBaseURL}/admin/settings/provider/team-detail/${id}`);
const getProviderTeamsOverview = (config) => getRequest(`${userBaseURL}/provider-teams/export`, config);
const getReports = (config = {}) => getRequest(`${userBaseURL}s/reports`, config);
const getScheduledReports = (config = {}) => getRequest(scheduledReportsBaseURL, config);
const getScheduledReport = (id) => getRequest(`${scheduledReportsBaseURL}/${id}`);
const getScheduledReportOptions = () => getRequest(`${scheduledReportsBaseURL}/options`);
const createScheduledReport = (payload) => postRequest(scheduledReportsBaseURL, payload);
const updateScheduledReport = (id, payload) => patchRequest(`${scheduledReportsBaseURL}/${id}`, payload);
const getSettings = () => getRequest(`${userBaseURL}/settings`);
const getShipmentTemplates = () => getRequest(`${userBaseURL}/templates`);
const getShipperCompanyDetails = () => getRequest(`${userBaseURL}/admin/settings/company/company-details`);
const getToken = () => postRequest(`${userBaseURL}/token`);
const getUserRoles = (config = {}) => getRequest(`${userBaseURL}/admin/settings/roles`, config);
const getUserRoleDetails = (id) => getRequest(`${userBaseURL}/admin/settings/roles/${id}`);
const getWidgets = (config) => getRequest(`${userBaseURL}/widgets`, config);
const getWorkflowSettings = () => getRequest(`${userBaseURL}/admin/settings/company/workflow`);
const getPaymentLocations = (config = {}) => getRequest(`${userBaseURL}/admin/settings/payment-locations`, config);
const getPaymentLocationDetails = (reference) => getRequest(`${userBaseURL}/admin/settings/payment-locations/${reference}`);
const getProviders = (location, config = {}) => getRequest(`${userBaseURL}/admin/settings/providers/${location}`, config);
const getParcelProviders = (location, config = {}) =>
  getRequest(`${userBaseURL}/admin/settings/parcel-providers/${location}`, config);
const getProviderDetails = (location, slug) => getRequest(`${userBaseURL}/admin/settings/providers/${location}/${slug}`);
const getPendingTasks = () => getRequest(`${userBaseURL}/has-tasks-pending`);
const resetPassword = (payload) => postRequest(`${userBaseURL}/reset-password`, payload);
const resetWidgets = () => postRequest(`${userBaseURL}/settings/reset`);
const updateAddress = (id, payload) => putRequest(`${userBaseURL}/address-book/${id}`, payload);
const updateApprovalLevel = (id, payload) => putRequest(`${userBaseURL}/admin/settings/approval-levels/${id}`, payload);
const updateContact = (id, payload) => putRequest(`${userBaseURL}/contacts/${id}`, payload);
const updateInvoiceSettings = (payload) => putRequest(`${userBaseURL}/admin/settings/company/invoice`, payload);
const updateNotificationSettings = (payload) => putRequest(`${userBaseURL}/settings/notifications`, payload);
const updatePaymentLocation = (reference, payload) =>
  putRequest(`${userBaseURL}/admin/settings/payment-locations/${reference}`, payload);
const updateProviderReference = (location, slug, data) =>
  patchRequest(`${userBaseURL}/admin/settings/providers/${location}/${slug}`, data);
const updateProviderTeams = (id, payload) => putRequest(`${userBaseURL}/admin/settings/provider/edit-team/${id}`, payload);
const updateUser = (payload) => putRequest(userBaseURL, payload);
const updateUserRoleDetails = (id, payload) => putRequest(`${userBaseURL}/admin/settings/roles/${id}`, payload);
const updatePrefill = (id, payload) => putRequest(`${userBaseURL}/admin/settings/prefill/${id}`, payload);
const updateSettings = (payload, config = {}) => postRequest(`${userBaseURL}/settings`, payload, config);
const updateShipperCompanyDetails = (payload) =>
  putRequest(`${userBaseURL}/admin/settings/company/company-details`, payload);
const postReportRequest = (payload) => postRequest(`${REACT_APP_CARGOBASE_BASE_URL}/users/reports`, payload);
const postShipmentRequest = (payload) => postRequest(`${REACT_APP_CARGOBASE_BASE_URL}/users/reports`, payload);
const updateWorkflowSettings = (payload) => putRequest(`${userBaseURL}/admin/settings/company/workflow`, payload);
const postQuoteRequest = (payload) => postRequest(`${REACT_APP_CARGOBASE_BASE_URL}/users/reports`, payload);
const postInvoiceRequest = (payload) => postRequest(`${REACT_APP_CARGOBASE_BASE_URL}/users/reports`, payload);
const deleteReport = (payload) => deleteRequest(`${REACT_APP_CARGOBASE_BASE_URL}/users/reports/${payload}`);
const deleteScheduledReport = (id) => deleteRequest(`${scheduledReportsBaseURL}/${id}`);
const deleteUserRole = (id, payload) => deleteRequest(`${userBaseURL}/admin/settings/roles/${id}`, payload);
const getTeamMemberList = (config = {}) => getRequest(`${userBaseURL}/admin/settings/users`, config);
const getTeamMemberListById = (payload) => getRequest(`${userBaseURL}/admin/settings/users/${payload}`);
const deactivateAccount = (payload) => deleteRequest(`${userBaseURL}/admin/settings/users/${payload}`);
const reactivateAccount = (payload) => postRequest(`${userBaseURL}/admin/settings/users/${payload}`);
const resendUserInvite = (email) => putRequest(`${userBaseURL}/admin/settings/users/${email}/re-invite`);
const updateTeamMemberDetail = (email, payload) => putRequest(`${userBaseURL}/admin/settings/users/${email}`, payload);
const getUserDetail = (email) => getRequest(`${userBaseURL}/admin/settings/users/${email}`);
const getInviteList = () => getRequest(`${userBaseURL}/admin/settings/users/invite`);
const getSupplierCompanySlugs = () => getRequest(`${userBaseURL}/admin/settings/users/supplier-company-slugs`);
const createInviteUser = (payload) => postRequest(`${userBaseURL}/admin/settings/users/invite`, payload);
const exportDetail = (config) => getRequest(`${userBaseURL}/admin/settings/users/export`, config);
const getUserCustomSettings = () => getRequest(`${userBaseURL}/custom-settings`);
const updateUserCustomSettings = (payload) => putRequest(`${userBaseURL}/custom-settings`, payload);
const submitUserCustomSettings = (payload) => postRequest(`${userBaseURL}/custom-settings/submit`, payload);
const getFilterPresets = () => getRequest(`${userBaseURL}/presets`);
const updateFilterPresets = (payload) => putRequest(`${userBaseURL}/presets`, payload);
const createFilterPresets = (payload) => postRequest(`${userBaseURL}/presets`, payload);
const deleteFilterPreset = (payload) => deleteRequest(`${userBaseURL}/presets`, payload);
const userActivation = (payload) => putRequest(`${userBaseURL}/verify-activation`, payload);
const getActivationUserDetail = (token, email) =>
  getRequest(`${userBaseURL}/verify-activation?token=${token}&email=${email}`);
const downloadImportsTemplate = () =>
  getRequest(`${userBaseURL}/import-templates/quote-request`, {
    responseType: "blob",
  });
const guestUserActivation = (payload) => putRequest(`${REACT_APP_CARGOBASE_BASE_URL}/guest-user/verify-activation`, payload);
const getActivationGuestUserDetail = (token, email) =>
  getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/guest-user/verify-activation?token=${token}&email=${email}`);
const downloadOffPlatformShipmentImportTemplate = () =>
  getRequest(`${userBaseURL}/import-templates/off-platform-shipment`, {
    responseType: "blob",
  });
const getImportsListing = (config = {}) => getRequest(`${userBaseURL}/import-objects-list`, config);
// setting custom timeout (5 mins) for admin/download-import-template template as quick fix because the data might be large
const downloadFileImportTemplate = (config = {}) =>
  getRequest(`${userBaseURL}/admin/download-import-template`, { ...config, responseType: "blob", timeout: 300000 });

// Status widget endpoints
const getBookedGraphData = (config = {}) => getRequest(`${userBaseURL}/status-widget/booked-graph`, config);
const getRecentUpdates = (config = {}) => getRequest(`${userBaseURL}/status-widget/notification`, config);
const getInvoiceStatusData = (config = {}) => getRequest(`${userBaseURL}/status-widget/invoice`, config);
const getOrderStatusData = (config = {}) => getRequest(`${userBaseURL}/status-widget/order`, config);
const getShipmentStatusData = (config = {}) => getRequest(`${userBaseURL}/status-widget/shipment`, config);
const getQuoteRequestStatusData = (config = {}) => getRequest(`${userBaseURL}/status-widget/quote-request`, config);
const getRecentBookmarks = (config = {}) => getRequest(`${userBaseURL}/status-widget/bookmark`, config);

// Developer API tokens
const getDeveloperApiTokens = (config = {}) => getRequest(`${userBaseURL}/developer/api-tokens`, config);
const createDeveloperApiToken = (payload) => postRequest(`${userBaseURL}/developer/api-tokens`, payload);
const revokeDeveloperApiToken = (id) => deleteRequest(`${userBaseURL}/developer/api-tokens/${id}`);

// Parcel accounts
const getParcelAccounts = (params) => getRequest(`${userBaseURL}/admin/settings/parcel-accounts`, params);
const getParcelAccountDetails = (id) => getRequest(`${userBaseURL}/admin/settings/parcel-accounts/${id}`);
const updateParcelAccount = (id, payload) => putRequest(`${userBaseURL}/admin/settings/parcel-accounts/${id}`, payload);
const validateFedexAddress = (payload) =>
  postRequest(`${userBaseURL}/admin/settings/parcel-accounts/fedex/address-validation`, payload);
const generateFedexPin = (payload) =>
  postRequest(`${userBaseURL}/admin/settings/parcel-accounts/fedex/generate-pin`, payload);
const validateFedexPin = (payload) =>
  postRequest(`${userBaseURL}/admin/settings/parcel-accounts/fedex/pin-validation`, payload);
const validateFedexInvoice = (payload) =>
  postRequest(`${userBaseURL}/admin/settings/parcel-accounts/fedex/invoice-validation`, payload);

// Account mappings
const getAccountMappings = (config = {}) => getRequest(`${userBaseURL}/admin/settings/account-mappings`, config);
const getAccountMappingPaymentLocations = (config = {}) =>
  getRequest(`${userBaseURL}/admin/settings/account-mappings/payment-locations`, config);
const createAccountMappings = (payload) => postRequest(`${userBaseURL}/admin/settings/account-mappings`, payload);
const updateAccountMapping = (id, payload) => putRequest(`${userBaseURL}/admin/settings/account-mappings/${id}`, payload);
const deleteAccountMapping = (id) => deleteRequest(`${userBaseURL}/admin/settings/account-mappings/${id}`);

export {
  userBaseURL,
  acceptLegalAgreements,
  addAddress,
  addContact,
  createApprovalLevel,
  createShipmentTemplate,
  createNewUserRole,
  createNewPaymentLocation,
  createPrefill,
  deleteAddress,
  deleteApprovalLevel,
  deleteContact,
  deletePrefill,
  deleteProvider,
  deleteShipmentTemplate,
  deleteUserRole,
  deleteAllApprovalLevels,
  exportProviderTeams,
  getAddressBook,
  getAddress,
  getAllReviews,
  getAllProviderContacts,
  getApprovalLevelCreateOptions,
  getApprovalLevelDetails,
  getBookedGraphData,
  getCompanyApprovalLevels,
  getContact,
  getContactBook,
  getCurrentUser,
  getDPA,
  getInvoiceSettings,
  getLocationApprovalLevels,
  getNewUserRoles,
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getNotificationSettings,
  getRecentlyDeliveredShipments,
  getRecentUpdates,
  getUsers,
  getPaymentLocations,
  getPaymentLocationDetails,
  getPermissions,
  getPreferredContact,
  getPrefills,
  getPrefillDetails,
  getProviders,
  getParcelProviders,
  getProviderDetails,
  getProvidersList,
  getProviderTeamsOverview,
  getProviderTeamsDetail,
  getReports,
  getScheduledReports,
  getScheduledReport,
  getScheduledReportOptions,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  getSettings,
  getShipmentTemplates,
  getShipperCompanyDetails,
  getToken,
  getUserRoles,
  getUserDetail,
  getUserRoleDetails,
  getWidgets,
  getWorkflowSettings,
  resetPassword,
  resetWidgets,
  updateAddress,
  updateApprovalLevel,
  updateContact,
  updateInvoiceSettings,
  updateNotificationSettings,
  updatePaymentLocation,
  updateProviderReference,
  updateProviderTeams,
  updatePrefill,
  updateUser,
  updateSettings,
  updateShipperCompanyDetails,
  updateUserRoleDetails,
  updateWorkflowSettings,
  getPendingTasks,
  getProviderTeams,
  postReportRequest,
  postShipmentRequest,
  postQuoteRequest,
  postInvoiceRequest,
  deleteReport,
  getTeamMemberList,
  getTeamMemberListById,
  deactivateAccount,
  reactivateAccount,
  updateTeamMemberDetail,
  getInviteList,
  getSupplierCompanySlugs,
  createInviteUser,
  exportDetail,
  getUserCustomSettings,
  resendUserInvite,
  updateUserCustomSettings,
  submitUserCustomSettings,
  getFilterPresets,
  updateFilterPresets,
  createFilterPresets,
  deleteFilterPreset,
  getImportsListing,
  downloadImportsTemplate,
  downloadOffPlatformShipmentImportTemplate,
  userActivation,
  getActivationUserDetail,
  downloadFileImportTemplate,
  guestUserActivation,
  getActivationGuestUserDetail,
  getInvoiceStatusData,
  getOrderStatusData,
  getShipmentStatusData,
  getQuoteRequestStatusData,
  getRecentBookmarks,
  getDeveloperApiTokens,
  createDeveloperApiToken,
  revokeDeveloperApiToken,
  getParcelAccounts,
  getParcelAccountDetails,
  updateParcelAccount,
  validateFedexAddress,
  generateFedexPin,
  validateFedexPin,
  validateFedexInvoice,
  getAccountMappings,
  getAccountMappingPaymentLocations,
  createAccountMappings,
  updateAccountMapping,
  deleteAccountMapping,
};
